import React from "react";
import { X, AlertCircle } from "lucide-react";

const ExtractedTicketCard = ({ ticket, index, onUpdate, onRemove, columns }) => {
  const priorityColors = {
    urgent: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-300 dark:border-red-800",
    high: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-300 dark:border-orange-800",
    medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-300 dark:border-yellow-800",
    low: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-300 dark:border-green-800",
  };

  // Generate status colors based on column colors
  const getStatusColorClass = (columnColor) => {
    const colorMap = {
      blue: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-300 dark:border-blue-800",
      green: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-300 dark:border-green-800",
      yellow: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-300 dark:border-yellow-800",
      red: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-300 dark:border-red-800",
      purple: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border-purple-300 dark:border-purple-800",
      pink: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400 border-pink-300 dark:border-pink-800",
      indigo: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-300 dark:border-indigo-800",
      gray: "bg-gray-100 text-gray-800 dark:bg-slate-700/50 dark:text-slate-300 border-gray-300 dark:border-slate-600",
    };
    return colorMap[columnColor] || "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300 border-gray-300 dark:border-slate-600";
  };

  // Find the current column to get its color
  const currentColumn = columns?.find((col) => col.status === ticket.status);
  const statusColorClass = currentColumn ? getStatusColorClass(currentColumn.color) : "bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300 border-gray-300 dark:border-slate-600";

  const handleChange = (field, value) => {
    onUpdate(index, { ...ticket, [field]: value });
  };

  return (
    <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-500 transition-all duration-200 relative group">
      {/* Remove Button */}
      <button
        onClick={() => onRemove(index)}
        className="absolute top-2 right-2 p-1 rounded-full bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove ticket"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Title Input */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
          Title
        </label>
        <input
          type="text"
          value={ticket.title}
          onChange={(e) => handleChange("title", e.target.value)}
          maxLength={200}
          className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-colors"
          placeholder="Enter ticket title..."
        />
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-500 dark:text-slate-500">
            {ticket.title.length}/200
          </span>
          {ticket.title.length === 0 && (
            <span className="text-xs text-red-600 dark:text-red-400 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              Title required
            </span>
          )}
        </div>
      </div>

      {/* Description Textarea */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
          Description
        </label>
        <textarea
          value={ticket.description}
          onChange={(e) => handleChange("description", e.target.value)}
          maxLength={10000}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs resize-none transition-colors"
          placeholder="Enter ticket description..."
        />
        <span className="text-xs text-gray-500 dark:text-slate-500">
          {ticket.description.length}/10000
        </span>
      </div>

      {/* Priority and Status Dropdowns */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
            Priority
          </label>
          <select
            value={ticket.priority}
            onChange={(e) => handleChange("priority", e.target.value)}
            className={`w-full h-8 px-2 border dark:border-slate-600 rounded-lg text-xs font-medium ${
              priorityColors[ticket.priority]
            } focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 transition-colors`}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
            Status
          </label>
          <select
            value={ticket.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={`w-full h-8 px-2 border dark:border-slate-600 rounded-lg text-xs font-medium ${statusColorClass} focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-slate-900 transition-colors`}
          >
            {columns && columns.length > 0 ? (
              columns.map((column) => (
                <option key={column._id} value={column.status}>
                  {column.name}
                </option>
              ))
            ) : (
              <option value={ticket.status}>{ticket.status}</option>
            )}
          </select>
        </div>
      </div>
    </div>
  );
};

export default ExtractedTicketCard;
